import { useState } from 'react';
// eslint-disable-next-line ferp-subgraph-ui/no-direct-mantine-import
import { Stack } from '@mantine/core';
import { PageHeader } from '../../components/PageHeader';
import { DataTable, type Column } from '../../components/DataTable';
import { StatusBadge } from '../../components/StatusBadge';
import { FilterBar } from '../../components/FilterBar';
import { useQualifications } from '../../hooks/useHr';

interface Qualification {
  id: string;
  employeeId: string;
  employeeName: string;
  name: string;
  type: string;
  issueDate: string | null;
  expiryDate: string | null;
  status: string;
}

interface QualificationsResponse {
  qualifications: {
    data: Qualification[];
    total: number;
  };
}

const QUALIFICATION_STATUS_COLORS: Record<string, string> = {
  valid: 'green',
  expired: 'red',
  pending: 'yellow',
};

export function QualificationListPage() {
  const [page, setPage] = useState(1);
  const pageSize = 25;

  const [search, setSearch] = useState('');
  const [filterValues, setFilterValues] = useState<Record<string, string>>({});

  const { data: queryData, loading } = useQualifications({
    page,
    perPage: pageSize,
    type: filterValues.type || undefined,
  });
  const qualData = queryData as QualificationsResponse | undefined;
  const data = qualData?.qualifications?.data ?? [];
  const total = qualData?.qualifications?.total ?? 0;

  const handleFilterChange = (key: string, value: string) => {
    setFilterValues((prev) => ({ ...prev, [key]: value }));
    setPage(1);
  };

  const formatType = (type: string) =>
    type
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (c) => c.toUpperCase());

  const columns: Column<Qualification>[] = [
    {
      key: 'employeeName',
      label: 'Employee',
      sortable: true,
      render: (row) => row.employeeName || row.employeeId,
    },
    {
      key: 'name',
      label: 'Qualification Name',
      sortable: true,
    },
    {
      key: 'type',
      label: 'Type',
      width: 140,
      render: (row) => formatType(row.type),
    },
    {
      key: 'issueDate',
      label: 'Issue Date',
      width: 120,
      render: (row) => row.issueDate ? new Date(row.issueDate).toLocaleDateString() : '-',
    },
    {
      key: 'expiryDate',
      label: 'Expiry Date',
      width: 120,
      render: (row) => row.expiryDate ? new Date(row.expiryDate).toLocaleDateString() : '-',
    },
    {
      key: 'status',
      label: 'Status',
      width: 110,
      render: (row) => (
        <StatusBadge status={row.status} colorMap={QUALIFICATION_STATUS_COLORS} />
      ),
    },
  ];

  return (
    <Stack gap="md">
      <PageHeader
        title="Qualifications"
        subtitle={`${total} qualifications`}
      />

      <FilterBar
        searchValue={search}
        onSearchChange={(val) => {
          setSearch(val);
          setPage(1);
        }}
        searchPlaceholder="Search qualifications..."
        filters={[
          {
            key: 'type',
            label: 'Type',
            options: [
              { value: 'education', label: 'Education' },
              { value: 'license', label: 'License' },
              { value: 'certification', label: 'Certification' },
              { value: 'skill', label: 'Skill' },
              { value: 'language', label: 'Language' },
            ],
          },
        ]}
        filterValues={filterValues}
        onFilterChange={handleFilterChange}
      />

      <DataTable<Record<string, unknown>>
        columns={columns as unknown as Column<Record<string, unknown>>[]}
        data={data as unknown as Record<string, unknown>[]}
        loading={loading}
        pagination={{
          page,
          pageSize,
          total,
          onPageChange: setPage,
        }}
        getRowKey={(row) => row.id as string}
        emptyMessage="No qualifications found"
      />
    </Stack>
  );
}

export default QualificationListPage;
